import { Card, Skeleton, theme } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import { SectionHeader } from './SectionHeader';

export interface DashboardCardProps {
  /** Header content. A string title with subtitle/extra renders as a SectionHeader. */
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Trailing content inside the SectionHeader (when title is a string). */
  extra?: ReactNode;
  size?: 'default' | 'small';
  /** Merged over the shared card base style (radius/border/shadow). */
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  /** Extra header styles (e.g. reduced padding). */
  headerStyle?: CSSProperties;
  /** Optional class for responsive layout of a SectionHeader. */
  headerClassName?: string;
  className?: string;
  /** Renders a skeleton body instead of children. */
  loading?: boolean;
  children: ReactNode;
}

/**
 * Shared card wrapper for dashboard sections: uniform radius, border and
 * shadow, plus an optional SectionHeader title and a passthrough body.
 */
export function DashboardCard({
  title,
  subtitle,
  extra,
  size,
  style,
  bodyStyle,
  headerStyle,
  headerClassName,
  className,
  loading = false,
  children,
}: DashboardCardProps) {
  const { token } = theme.useToken();

  let header: ReactNode;
  if (title === undefined) {
    header = undefined;
  } else if (typeof title === 'string' && (subtitle !== undefined || extra !== undefined)) {
    header = (
      <SectionHeader
        className={headerClassName}
        title={title}
        subtitle={typeof subtitle === 'string' ? subtitle : undefined}
        trailing={extra}
      />
    );
  } else {
    header = title;
  }

  return (
    <Card
      size={size}
      className={className}
      title={header}
      style={{
        borderRadius: 12,
        border: `1px solid ${token.colorBorderSecondary}`,
        boxShadow: token.boxShadow,
        ...style,
      }}
      styles={{
        header: {
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          padding: '16px 22px',
          ...headerStyle,
        },
        body: bodyStyle,
      }}
    >
      {loading ? <Skeleton active paragraph={{ rows: 4 }} /> : children}
    </Card>
  );
}
